import { useState } from 'react';
import { Navigate, useParams } from 'react-router';

import { useGetProjectQuery } from '../api/projects';
import ProjectTabBar from './ProjectTabBar';
import SubProjectCapabilitiesSection from './SubProjectCapabilitiesSection';
import SubProjectIdentityCard from './SubProjectIdentityCard';
import TabParametros from './TabParametros';
import { ProjectTab, isSubProjectDetail } from './project-detail.types';

interface Tab {
  id: ProjectTab;
  label: string;
}

interface Props {
  projectId?: number | string | null;
}

const ProjectConfigView = ({ projectId: projectIdProp }: Props) => {
  const { masterProjectId, subProjectId } = useParams<{ masterProjectId: string; subProjectId: string }>();
  const projectId = projectIdProp ?? subProjectId;
  const [activeTab, setActiveTab] = useState<ProjectTab>('funcionalidades');

  const {
    data: project,
    isLoading,
    isError,
  } = useGetProjectQuery(Number(projectId), {
    skip: !projectId,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <div className="h-16 bg-bg-surface-raised rounded-xl animate-pulse" />
        <div className="h-96 bg-bg-surface-raised rounded-xl animate-pulse" />
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className="bg-error-light border border-error-light rounded-xl px-6 py-5 text-sm text-error">
        No se pudo cargar la configuración del proyecto.
      </div>
    );
  }

  if (!isSubProjectDetail(project)) {
    return <Navigate to={`/project/${project.id}/config`} replace />;
  }

  if (masterProjectId !== undefined && Number(masterProjectId) !== project.masterProjectId) {
    return (
      <div className="bg-error-light border border-error-light rounded-xl px-6 py-5 text-sm text-error">
        El subproyecto no pertenece a la inmobiliaria seleccionada.
      </div>
    );
  }

  const tabs: Tab[] = [
    { id: 'funcionalidades', label: 'Funcionalidades' },
    { id: 'parametros', label: 'Parámetros' },
  ];

  return (
    <div className="flex flex-col gap-5">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-text-subtle mb-1 font-body">Subproyecto</p>
          <h2 className="text-2xl font-bold text-text-default m-0 font-display">{project.name}</h2>
          <div className="flex items-center gap-2 mt-1.5">
            <span className="text-sm text-text-muted font-body">
              ID: {project.id} · {project.countryCode}
            </span>
            <span
              className={`inline-flex items-center text-xs font-semibold px-2.5 py-1 rounded-full ${
                project.isActive ? 'bg-success-light text-success' : 'bg-warning-light text-warning'
              }`}
            >
              {project.isActive ? 'Activo' : 'Inactivo'}
            </span>
          </div>
        </div>
      </header>

      <SubProjectIdentityCard project={project} />

      <div className="bg-bg-surface border border-border-subtle rounded-xl overflow-hidden shadow-sm">
        <ProjectTabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

        <div className="p-6">
          {activeTab === 'funcionalidades' && <SubProjectCapabilitiesSection project={project} />}
          {activeTab === 'parametros' && <TabParametros project={project} />}
        </div>
      </div>
    </div>
  );
};

export default ProjectConfigView;
